import { useMutation, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { globalQueryKeys as queryKeys } from '@opallabs/api'
import {
  findResourceOfType,
  type OpalApiRequestBody,
  type OpalApiResponseDocument,
  workspaces,
} from '@opallabs/api/v3'

export type UseUpdateWorkspaceDefaultsParams =
  OpalApiRequestBody<'UpdateWorkspaceDefaultsV3'>['data']['attributes']

export const useUpdateWorkspaceDefaults = ({
  workspaceId,
  workspaceDefaultsId,
  onMutate,
  onSuccess,
}: {
  workspaceId: string
  workspaceDefaultsId: string

  onMutate?: (
    newDefaults: UseUpdateWorkspaceDefaultsParams
  ) => void | Promise<void>
  onSuccess?: () => void | Promise<void>
}) => {
  const queryClient = useQueryClient()
  const queryKey = queryKeys.workspaceDefaults(workspaceId)

  return useMutation({
    mutationKey: queryKey,
    useErrorBoundary: true,
    mutationFn: async (newDefaults: UseUpdateWorkspaceDefaultsParams) =>
      workspaces.updateDefaults(
        { path: { workspace_defaults_id: workspaceDefaultsId } },
        {
          data: {
            id: workspaceDefaultsId,
            type: 'workspace_defaults',
            attributes: newDefaults,
          },
        }
      ),
    onMutate: async (newDefaults) => {
      queryClient.setQueryData<OpalApiResponseDocument<'ReadWorkspaceV3', 200>>(
        queryKey,
        (cachedData) =>
          produce(cachedData, (draftDocument) => {
            const workspaceDefaults = draftDocument?.included?.find(
              findResourceOfType(
                'workspace_defaults',
                'V3',
                workspaceDefaultsId
              )
            )

            if (!workspaceDefaults) {
              return
            }

            if (newDefaults.new_boards_inherit_workspace != null) {
              workspaceDefaults.attributes.new_boards_inherit_workspace =
                newDefaults.new_boards_inherit_workspace
            }

            if (newDefaults.new_plans_inherit_workspace != null) {
              workspaceDefaults.attributes.new_plans_inherit_workspace =
                newDefaults.new_plans_inherit_workspace
            }

            if (newDefaults.new_stories_inherit_workspace != null) {
              workspaceDefaults.attributes.new_stories_inherit_workspace =
                newDefaults.new_stories_inherit_workspace
            }

            if (newDefaults.restrict_content_email_notifications != null) {
              workspaceDefaults.attributes.restrict_content_email_notifications =
                newDefaults.restrict_content_email_notifications
            }

            if (newDefaults.disable_permission_notifications != null) {
              workspaceDefaults.attributes.disable_permission_notifications =
                newDefaults.disable_permission_notifications
            }
          })
      )

      await onMutate?.(newDefaults)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey })
      await onSuccess?.()
    },
  })
}
